import { ServiceContractTypes } from '@uptime-bff/api-contract';
import { darkTheme, ThemeColor } from 'hfs-common-ui';
import i18next from 'i18next';
import { DefaultTheme } from 'styled-components';

export type AlertVariant = 'error' | 'warning' | 'success' | 'info';

/**
 * Returns the theme-aware border color for an AlertBanner variant.
 */
export const getAlertBorderColor = (theme: DefaultTheme, variant: AlertVariant): string => {
  const isDark = theme === darkTheme;
  switch (variant) {
    case 'error':
      return isDark ? theme.colors.red200 : theme.colors.red500;
    case 'warning':
      return isDark ? theme.colors.yellow300 : theme.colors.yellow500;
    case 'success':
      return isDark ? theme.colors.green300 : theme.colors.green500;
    case 'info':
    default:
      return theme.colors.onSurface;
  }
};

/**
 * Returns the theme-aware icon `ThemeColor` for an AlertBanner variant.
 */
export const getAlertIconColor = (theme: DefaultTheme, variant: AlertVariant): ThemeColor => {
  const isDark = theme === darkTheme;
  switch (variant) {
    case 'error':
      return isDark ? 'red300' : 'red500';
    case 'warning':
      return isDark ? 'yellow300' : 'yellow500';
    case 'success':
      return isDark ? 'green300' : 'green500';
    case 'info':
    default:
      return 'onSurface';
  }
};

export const removeGACookies = () => {
  // Get domain without subdomain (e.g., "example.com" from "app.example.com")
  const domainParts = globalThis.location.hostname.split('.');
  const rootDomain = domainParts.length > 1 ? domainParts.slice(-2).join('.') : globalThis.location.hostname;

  // All GA cookie names
  const gaCookies = ['_ga', '_ga_'];

  // Find and remove any GA measurement ID specific cookies
  const allCookies = document.cookie.split('; ');
  allCookies.forEach((cookie) => {
    const cookieName = cookie.split('=')[0];
    if (cookieName.startsWith('_ga')) {
      gaCookies.push(cookieName);
    }
  });

  // Remove all GA cookies with different domain options
  gaCookies.forEach((cookieName) => {
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${rootDomain}`;
  });
};

/**
 * Sets user consent preferences for Microsoft Clarity (Consent V2).
 *
 * Required parameters:
 * @param ads - Consent for ads related storage: 'denied' | 'granted'.
 * @param analytics - Consent for analytics related storage: 'denied' | 'granted'.
 *
 * Use 'denied' to prevent storage, 'granted' to allow it.
 * Calling this repeatedly updates previously sent values.
 */

export const clarityConcentV2 = (ads: string, analytics: string) => {
  // @ts-expect-error
  window.clarity('consentv2', {
    ad_Storage: ads,
    analytics_Storage: analytics,
  });
};

export const convertCountryCodeToCountryName = (countryCodes?: string): string | undefined => {
  if (!countryCodes) {
    return undefined;
  }
  const countryName = new Intl.DisplayNames([i18next.language ?? 'en'], { type: 'region' });
  return countryName.of(countryCodes);
};

/*
  i18format is a helper function for formatting internationalized strings with placeholders.
  This will help us use {{placeholder}} and {placeholder}
*/
export const i18format = (_err: unknown, _key: string, res: string, options: Record<string, unknown>): string => {
  return typeof res === 'string'
    ? res.replaceAll(/\{\{\s*(\w+)\s*\}\}/g, (match, name: string) => {
        const value = options?.[name];
        return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
          ? String(value)
          : match;
      })
    : res;
};


/**
 * A set of valid service contract types for maintenance assets.
 */
export const serviceContractValues = new Set<string>(Object.values(ServiceContractTypes));
